import type { CorePlanTier } from "@functions/plans/types";
import type { PlanCycleHistoricalUsageData } from "@functions/services/billingUsage/planCycleHistoricalUsage";
import type { PaidPlanSubscriptionData, WorkspaceUsage } from "@functions/state/types";
import { Plan } from "@functions/plans/types";
import { computePrepaidCreditPacks } from "@functions/plans/information";
import { getWorkspacePlanLimits } from "../../../../../clientServices/workspaceLimits";
import { getIncludedCredits, getPricePerCredit } from "../../../../utils/ubpUtils";
import { computeProratedPrice, type TierChangeOverride } from "../../../../utils/prorateUtils";
import type { SeatAddonInfo } from "../../../hooks/useSeatAddonInfo";

type CoreSubscription = Pick<
    PaidPlanSubscriptionData,
    "planUnitAmount" | "periodStart" | "renewedAt"
>;

type GetCoreTierChangeOverrideArgs = {
    usage: WorkspaceUsage | null | undefined;
    currentTier: CorePlanTier | null | undefined;
    selectedTier: CorePlanTier;
    isAnnual: boolean;
    subscription: CoreSubscription | null | undefined;
    planHistoricalUsageData: PlanCycleHistoricalUsageData | null | undefined;
    coreSeatInfo: SeatAddonInfo | null | undefined;
};

export function getCoreTierChangeOverride({
    usage,
    currentTier,
    selectedTier,
    isAnnual,
    subscription,
    planHistoricalUsageData,
    coreSeatInfo
}: GetCoreTierChangeOverrideArgs): TierChangeOverride | null {
    if (!subscription || !currentTier) return null;
    if (selectedTier.credits <= currentTier.credits) return null;

    const periodEnd = usage?.activePlanSubscription?.currentPeriodEnd;
    const periodStart = usage?.activePlanSubscription?.currentPeriodStart;
    const renewalDate = periodEnd
        ? new Date(periodEnd.seconds ? periodEnd.seconds * 1000 : periodEnd).toLocaleDateString(
              "en-US",
              { day: "numeric", month: "short", year: "numeric" }
          )
        : "next billing cycle";

    const currentCredits = isAnnual ? currentTier.credits * 12 : currentTier.credits;

    const newTierPrice = isAnnual ? selectedTier.annualPrice * 12 : selectedTier.monthlyPrice;
    const currentTierPrice = subscription.planUnitAmount
        ? subscription.planUnitAmount / 100
        : isAnnual
          ? currentTier.annualPrice * 12
          : currentTier.monthlyPrice;

    const basePricePerCredit = getPricePerCredit({ usage });
    const dynamicPacks = computePrepaidCreditPacks(basePricePerCredit, "core");
    const pricePerCredit =
        dynamicPacks.find((p) => p.credits === currentTier.credits)?.pricePerCredit ??
        basePricePerCredit;

    const includedCredits = getIncludedCredits({ usage });
    const cycleCreditsUsed = planHistoricalUsageData?.cycleCredits ?? 0;
    const unusedCredits = Math.max(0, includedCredits - cycleCreditsUsed);

    const cycleStartSeconds = periodStart?.seconds ?? subscription.periodStart ?? 0;
    const cycleEndSeconds = periodEnd?.seconds ?? subscription.renewedAt ?? 0;

    const currentCollabLimit = usage?.limit?.collaborators ?? 0;
    const planDefaultSeats = getWorkspacePlanLimits(Plan.UbpCore, true)?.collaborators ?? 0;
    const purchasedSeatAddons = Math.max(0, currentCollabLimit - planDefaultSeats);
    const seatMonthlyPrice = coreSeatInfo?.pricePerSeat ?? 0;
    const addOnCost = purchasedSeatAddons * seatMonthlyPrice * (isAnnual ? 12 : 1);

    return {
        renewalDate,
        currentCredits,
        newTierPrice,
        currentTierPrice,
        pricePerCredit,
        unusedCredits,
        cycleCreditsUsed,
        cycleStartSeconds,
        cycleEndSeconds,
        isAnnual,
        addOnCost
    };
}

type TrackBundleChangeArgs = {
    usage: WorkspaceUsage | null | undefined;
    direction: "upgrade" | "downgrade";
    tierChangeOverride: TierChangeOverride | null;
    extraData?: Record<string, unknown>;
};

export function trackBundleChange({
    usage,
    direction,
    tierChangeOverride,
    extraData
}: TrackBundleChangeArgs): void {
    const proratedPrice = tierChangeOverride ? computeProratedPrice(tierChangeOverride) : null;
    window?.eventTracker?.event({
        type: direction === "upgrade" ? "Clicked Upgrade Bundle" : "Clicked Downgrade Bundle",
        data: {
            workspaceId: usage?.id,
            currentPlan: usage?.plan,
            initialPrice: tierChangeOverride?.newTierPrice ?? null,
            proratedPrice,
            ...extraData
        }
    });
}
